export const ADMIN_SUPPORT_ACTION_TYPES = [
  "force_password_reset",
  "force_signout",
  "lock_account",
  "unlock_account",
  "data_export",
  "account_deletion",
  // WF-56 — account later identified as belonging to a minor.
  "age_policy_suspend",
  "override_membership_state",
  "extend_trial",
  "override_commitment",
  "convert_trial",
  "rebind_hardware",
  "mark_hardware_lost",
  "blacklist_hardware",
  "issue_reclaim_label",
  "mark_reclaim_received",
  "refurb_qc",
  "override_trial_block",
  "household_exception",
  "submit_dispute_evidence",
  "resolve_dispute",
] as const;

export type AdminSupportActionType =
  (typeof ADMIN_SUPPORT_ACTION_TYPES)[number];

export interface AdminSupportActionRequest<TPayload = unknown> {
  actionType: AdminSupportActionType;
  targetUserId: string;
  reason: string;
  idempotencyKey: string;
  payload?: TPayload;
}

/**
 * Queue row for the Support Actions page — one (userId × actionType) pair
 * that the user could plausibly need a support workflow for. Backed by a
 * mock fixture today; will become a paginated server endpoint when
 * mutations come online.
 *
 * Note: this is the historical "placeholder" shape returned by the
 * /admin/support-actions/placeholders mock endpoint. The richer
 * `AdminSupportActionQueueRow` below is what `/admin/support-actions/queue`
 * returns and what feature pages prefer when they need extra context
 * (reason / severity / membershipState / accountStatus).
 */
export interface AdminSupportActionPlaceholder {
  userId: string;
  displayName: string;
  email: string;
  actionType: AdminSupportActionType;
}

export type AdminSupportActionSeverity = "info" | "warn" | "crit" | "ok";

export const SUPPORT_ACTION_QUEUE_STATES = [
  "pending",
  "blocked",
  "processing",
  "ready",
  "completed",
  "auto_unlocked",
  "failed",
] as const;

export type SupportActionQueueState =
  (typeof SUPPORT_ACTION_QUEUE_STATES)[number];

/**
 * Row returned by `GET /admin/support-actions/queue`. Derived from real
 * backend state (locked users, pending deletions, reclaims in flight,
 * past-due memberships). Extends the placeholder shape with operator
 * context. The `actionType` is a string rather than the enum because new
 * derivation sources may emit codes not yet in the enum; render code can
 * narrow with `isSupportedWorkflowType()`.
 */
export interface AdminSupportActionQueueRow {
  queueId: string;
  userId: string;
  displayName: string;
  email: string;
  actionType: AdminSupportActionType | string;
  severity: AdminSupportActionSeverity;
  source: string;
  state: SupportActionQueueState;
  reason: string;
  policy?: string | null;
  /** Human-readable relative time for mock / list display */
  requestedLabel: string;
  detectedAt?: string | null;
  membershipState?: string | null;
  accountStatus?: string | null;
}
